/**
 * @file index.tsx
 *
 * @fileoverview Renders job wizard bundles picker view.
 */
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import JobWizardSteps from '~/jobiqo/components/Job/Wizard/components/JobWizardSteps';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import { getJobUpradeIcon } from '~/jobiqo/components/Job/Wizard/Steps/Upgrades/utils/upgradesIcons';
import { WizardLayout } from '~/jobiqo/components/Wizard/Layout';
import { JobBundle } from '~/jobiqo/models/jobs';
import Price from '~/jobiqo/models/price';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { CenterBox } from '~/vcl/atoms/06-layout/01-alignment/02-center-box';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { ScrollToElement } from '~/vcl/atoms/06-layout/11-scroll-to-element';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { LoadingCircleIcon } from '~/vcl/icons/LoadingCircle';
import { FormActions } from '~/vcl/molecules/01-form/01-form-actions';
import { LongBoxSelectable } from '~/vcl/organisms/02-actions/03-long-box-selectable';
import styles from './bundles.module.scss';
import { CartMetaData } from '~/jobiqo/types/types';
import { hasFlatRate } from '../../../components/JobCartMetaData/utils';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { InfoIcon } from '~/vcl/icons/Info';
import { JobWizardCurrentOrg } from '~/jobiqo/components/Job/Wizard/components/JobWizardCurrentOrg';
import { getFrontendSiteName } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface JobWizardBundlesViewProps {
  /**
   * The available job bundles.
   */
  availableBundles: JobBundle[];
  /**
   * The allowed job bundles.
   */
  allowedBundles: JobBundle[];
  /**
   * The error component.
   */
  Error: React.ReactNode | null;
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * The onSubmit callback.
   */
  onSubmit: (upgrade: number | null) => void;
  /**
   * If the wizard is in republishing mode.
   *
   * @default "new"
   */
  flow?: JobWizardFlow;
  /**
   *
   */
  cartMetadata: CartMetaData;
}

export default function JobWizardBundlesView({
  availableBundles,
  allowedBundles,
  Error,
  loading,
  onSubmit,
  flow,
  cartMetadata,
}: JobWizardBundlesViewProps) {
  const { JOBS } = useFrontendConfig();
  const SHOW_DESTINATION_STEP = JOBS.JOB_POSTING_WIZARD_SHOW_DESTINATION;
  const [selectedBundle, setSelectedBundle] = useState<number | null>(
    cartMetadata?.defaultProduct?.id || null,
  );

  useEffect(() => {
    if (cartMetadata?.defaultProduct?.id) {
      // Make the default product selected by default.
      setSelectedBundle(cartMetadata?.defaultProduct?.id);
    }
  }, [cartMetadata?.defaultProduct?.id]);

  return (
    <WizardLayout size="small" Steps={<JobWizardSteps flow={flow} />}>
      {Error && (
        <ScrollToElement>
          {Error}
          <Spacing size="medium" />
        </ScrollToElement>
      )}
      <JobWizardCurrentOrg prefixLabel />
      <Text
        textTransform="uppercase"
        fontWeight={600}
        fontSize={2}
        color="gray8"
      >
        <FormattedMessage
          id="job_wizard_bundles_title"
          defaultMessage="Job posting products"
        />
      </Text>
      <Text color="gray5" fontSize={2} marginTop={6}>
        <FormattedMessage
          id="job_wizard_bundles_instructions"
          defaultMessage="Here, you can choose optional job products that will be applied to your order.
          {br}Of course, if you don't want any products, feel free to continue.
          {br}{br}Available products:"
          values={{ br: <br /> }}
        />
      </Text>
      {loading && (
        <>
          <CenterBox>
            <LoadingCircleIcon
              color="primary"
              height="100"
              width="100"
              title={
                <FormattedMessage
                  id="loading_message"
                  defaultMessage="Loading"
                />
              }
            />
          </CenterBox>
        </>
      )}
      {availableBundles.map(
        ({
          id,
          name,
          description,
          priceCurrency,
          priceNumber,
          icon,
          iconCustom,
          productCanBePaidWithCredits,
          excludeJobFlatrateSubscription,
          frontends,
        }: JobBundle) => {
          const BundlePrice = new Price(priceNumber as string, priceCurrency);
          const isUpgradeSelected = selectedBundle === id;
          const disabled = !allowedBundles.find((bundle) => bundle.id === id);

          let includedInFlatRate = hasFlatRate(
            cartMetadata,
            excludeJobFlatrateSubscription,
          );

          // If the destination step is active also check the bundle frontends.
          // When the subscription does not have any FE it means it is valid for
          // all frontends, so we should also check for that.
          let hasFlatrateForFrontend = false;
          if (includedInFlatRate && SHOW_DESTINATION_STEP) {
            hasFlatrateForFrontend =
              frontends?.some((frontend) =>
                cartMetadata?.activeJobFlatrateSubscriptions?.some(
                  (subscription) =>
                    subscription?.frontend === frontend ||
                    subscription?.frontend === null,
                ),
              ) || false;

            includedInFlatRate = includedInFlatRate && hasFlatrateForFrontend;
          }

          return (
            <Spacing margin={0} key={id}>
              <div className="job-wizard-bundle">
                <LongBoxSelectable
                  buttonId={id.toString()}
                  icon={getJobUpradeIcon(icon, iconCustom)}
                  selected={isUpgradeSelected}
                  onClick={() => {
                    setSelectedBundle(isUpgradeSelected ? null : id);
                  }}
                  title={name}
                  disabled={disabled}
                >
                  <Grid columns={[1, 4]}>
                    {description && (
                      <Cell gridColumn={'span 3'}>
                        <WYSIWYGText format="full_html" content={description} />
                      </Cell>
                    )}
                    <Cell className={styles.smallCell} justifyContent="center">
                      <Text
                        fontSize={5}
                        fontWeight="bold"
                        color={disabled ? 'gray3' : 'gray9'}
                        textAlign="center"
                      >
                        {includedInFlatRate || hasFlatrateForFrontend ? (
                          <span
                            style={{
                              textDecoration: 'line-through',
                            }}
                          >
                            {BundlePrice.getPriceRepresentation()}
                          </span>
                        ) : productCanBePaidWithCredits?.amount ? (
                          productCanBePaidWithCredits?.amount > 1 ? (
                            <FormattedMessage
                              id="job_wizard_bundles_job_credits_plural"
                              defaultMessage="{amount} job credits"
                              values={{
                                amount: productCanBePaidWithCredits?.amount,
                              }}
                            />
                          ) : (
                            <FormattedMessage
                              id="job_wizard_bundles_job_credits_singular"
                              defaultMessage="{amount} job credit"
                              values={{
                                amount: productCanBePaidWithCredits?.amount,
                              }}
                            />
                          )
                        ) : (
                          <span>{BundlePrice.getPriceRepresentation()}</span>
                        )}
                      </Text>
                      <Spacing />
                    </Cell>
                  </Grid>
                  {includedInFlatRate ? (
                    <FlexItems
                      gap={5}
                      alignItems="center"
                      data-test-id="job-bundle-included-from-flat-rate"
                    >
                      <InfoIcon color="primary" />
                      <FormattedMessage
                        id="job_bundles_product_included_flat_rate"
                        defaultMessage="Included in your flat rate subscription"
                      />
                    </FlexItems>
                  ) : SHOW_DESTINATION_STEP && hasFlatrateForFrontend ? (
                    <>
                      <FlexItems
                        gap={5}
                        alignItems="center"
                        data-test-id="job-bundle-included-from-flat-rate-frontend"
                      >
                        <InfoIcon color="primary" />
                        <FormattedMessage
                          id="job_bundles_product_included_flat_rate_for"
                          defaultMessage="Included in your flat rate subscription for:"
                        />
                      </FlexItems>
                      <Text color="primary">
                        {frontends
                          ?.filter((frontend) =>
                            cartMetadata?.activeJobFlatrateSubscriptions?.some(
                              (subscription) =>
                                subscription?.frontend === frontend,
                            ),
                          )
                          .map(getFrontendSiteName)
                          .join(', ')}
                      </Text>
                    </>
                  ) : null}
                </LongBoxSelectable>
              </div>
            </Spacing>
          );
        },
      )}
      <Spacing size="large" />
      <FormActions orientation="right">
        <Button
          type="button"
          buttonStyle="primary"
          disabled={loading}
          onClick={() =>
            onSubmit(
              // Don't use the bundle id if the user selects default product.
              availableBundles.find((bundle) => bundle.id === selectedBundle)
                ?.isDefault
                ? null
                : selectedBundle,
            )
          }
          data-test-id="job-wizard-bundle-continue"
        >
          <FormattedMessage
            id="job_wizard_continue"
            defaultMessage="Continue"
          />
        </Button>
      </FormActions>
    </WizardLayout>
  );
}

export { JobWizardBundlesView };
